import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { PoundSterling, TrendingUp, Layers, Pencil, Check, X, Wand2, Target } from "lucide-react";

const THEME_LABELS = {
  t_strategic: "Strategic", t_operational: "Operational", t_cyber: "Cyber",
  t_thirdparty: "Third-party", t_regulatory: "Regulatory", t_geopolitical: "Geopolitical",
  t_financial: "Financial",
};

const gbp = (v) => `£${new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 }).format(v || 0)}`;
const gbpFull = (v) => `£${new Intl.NumberFormat("en-GB").format(Math.round(v || 0))}`;

const Kpi = ({ icon: Icon, label, value, sub, tone = "text-ek-purple", testid }) => (
  <div data-testid={testid} className="rounded-md border border-black/5 bg-white px-5 py-4">
    <div className="flex items-center gap-2 text-ek-ink/65">
      <Icon size={15} className={tone} />
      <span className="text-[0.7rem] font-semibold uppercase tracking-wide">{label}</span>
    </div>
    <p className={`mt-2 text-3xl font-bold ${tone}`}>{value}</p>
    {sub && <p className="mt-0.5 text-xs text-ek-ink/65">{sub}</p>}
  </div>
);

const EconEditor = ({ control, onSaved, onCancel }) => {
  const [cost, setCost] = useState(control.annual_cost_gbp);
  const [share, setShare] = useState(control.mitigation_share);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/controls/${control.control_id}/economics`, {
        annual_cost_gbp: Number(cost), mitigation_share: Number(share),
      });
      toast.success("Economics updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="econ-editor">
      <input type="number" min="0" value={cost} onChange={(e) => setCost(e.target.value)}
        data-testid="econ-cost-input"
        className="w-24 rounded border border-black/15 px-2 py-1 text-xs outline-none focus:border-ek-purple" />
      <input type="number" min="0" max="0.95" step="0.01" value={share} onChange={(e) => setShare(e.target.value)}
        data-testid="econ-share-input" title="Mitigation share (0-0.95)"
        className="w-16 rounded border border-black/15 px-2 py-1 text-xs outline-none focus:border-ek-purple" />
      <button onClick={save} disabled={saving} data-testid="econ-save" className="text-emerald-600 hover:text-emerald-700"><Check size={15} /></button>
      <button onClick={onCancel} className="text-ek-ink/65 hover:text-red-600"><X size={15} /></button>
    </div>
  );
};

export default function ControlROI() {
  const { user } = useAuth();
  const canWrite = hasPermission(user, "risk.write");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [budget, setBudget] = useState(300000);
  const [optimising, setOptimising] = useState(false);
  const [optimisation, setOptimisation] = useState(null);

  const optimise = async () => {
    if (!budget || Number(budget) <= 0) { toast.error("Enter a budget greater than 0"); return; }
    setOptimising(true);
    try {
      const { data } = await api.get(`/controls/roi/optimise?budget=${Number(budget)}`);
      setOptimisation(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setOptimising(false);
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/controls/roi")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load control ROI"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const s = data?.summary;
  const items = data?.items || [];
  const chartData = items.slice(0, 10).map((i) => ({
    name: i.control_id.replace("CTL-", ""), reduction: i.ale_reduction_gbp,
    cost: i.annual_cost_gbp, roi: i.roi_ratio,
  }));

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-12" data-testid="control-roi-page">
        <p className="ek-eyebrow">Control ROI · Risk-spend justification</p>
        <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Where every control £ goes</h1>
        <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
          Each control is linked to the FAIR expected annual loss (ALE) it removes — so you can see, and defend,
          how much loss is avoided per £ spent. Effectiveness scales with control maturity.
        </p>

        {loading ? (
          <p className="py-24 text-center text-sm text-ek-ink/65">Calculating loss-exceedance across the portfolio…</p>
        ) : (
          <>
            {s && (
              <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <Kpi testid="kpi-spend" icon={PoundSterling} label="Annual control spend" value={gbp(s.total_spend_gbp)} tone="text-ek-ink" />
                <Kpi testid="kpi-reduction" icon={TrendingUp} label="Expected loss removed" value={gbp(s.total_ale_reduction_gbp)} tone="text-emerald-600" />
                <Kpi testid="kpi-roi" icon={Layers} label="Blended ROI" value={`${s.blended_roi}×`} sub="£ loss avoided per £ spent" />
                <Kpi testid="kpi-best" icon={TrendingUp} label="Best ROI control" value={s.best ? `${s.best.roi_ratio}×` : "—"} sub={s.best?.control_id} />
              </div>
            )}

            {chartData.length > 0 && (
              <div className="mt-6 rounded-md border border-black/5 bg-white p-5" data-testid="roi-chart">
                <p className="ek-eyebrow">Top 10 controls</p>
                <h3 className="font-display text-lg text-ek-ink">Expected annual loss removed (£)</h3>
                <div className="mt-3" style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b6b78" }} />
                      <YAxis tickFormatter={gbp} tick={{ fontSize: 11, fill: "#6b6b78" }} width={52} />
                      <Tooltip
                        formatter={(v, n) => n === "reduction" ? [gbpFull(v), "Loss removed"] : [v, n]}
                        labelFormatter={(l) => `Control CTL-${l}`}
                        contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Bar dataKey="reduction" radius={[4, 4, 0, 0]}>
                        {chartData.map((e, i) => <Cell key={i} fill={e.roi >= 1 ? "#7737F6" : "#dc2626"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="mt-6 rounded-md border border-ek-purple/20 bg-ek-purple/5 p-5" data-testid="spend-optimiser">
              <div className="flex items-center gap-2">
                <Wand2 size={16} className="text-ek-purple" />
                <h3 className="font-display text-lg text-ek-ink">Spend optimiser</h3>
              </div>
              <p className="mt-1 text-sm text-ek-ink/65">
                Set a budget and ERIO picks which controls to mature for the maximum expected-loss reduction (best marginal ROI first).
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-ek-ink/65">£</span>
                  <input type="number" min="0" step="50000" value={budget} onChange={(e) => setBudget(e.target.value)}
                    data-testid="optimiser-budget-input"
                    className="w-40 rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple" />
                </div>
                <button onClick={optimise} disabled={optimising} data-testid="optimise-button" className="ek-btn flex items-center gap-2">
                  <Target size={15} /> {optimising ? "Optimising…" : "Optimise spend"}
                </button>
              </div>

              {optimisation && (
                <div className="mt-4" data-testid="optimiser-result">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div><p className="text-[0.65rem] uppercase tracking-wide text-ek-ink/65">Investment</p><p className="text-lg font-bold text-ek-ink">{gbp(optimisation.total_investment_gbp)}</p></div>
                    <div><p className="text-[0.65rem] uppercase tracking-wide text-ek-ink/65">Extra loss removed</p><p className="text-lg font-bold text-emerald-600">{gbp(optimisation.total_ale_reduction_gain_gbp)}</p></div>
                    <div><p className="text-[0.65rem] uppercase tracking-wide text-ek-ink/65">Marginal ROI</p><p className="text-lg font-bold text-ek-purple">{optimisation.marginal_roi}×</p></div>
                    <div><p className="text-[0.65rem] uppercase tracking-wide text-ek-ink/65">Budget left</p><p className="text-lg font-bold text-ek-ink/60">{gbp(optimisation.remaining_budget_gbp)}</p></div>
                  </div>
                  {optimisation.recommended.length === 0 ? (
                    <p className="mt-3 text-sm text-ek-ink/65">No controls fit this budget — try increasing it.</p>
                  ) : (
                    <div className="mt-3 space-y-1.5">
                      {optimisation.recommended.map((c) => (
                        <div key={c.control_id} data-testid={`optimise-pick-${c.control_id}`} className="flex items-center justify-between rounded border border-black/5 bg-white px-3 py-2 text-sm">
                          <span className="text-ek-ink">{c.title} <span className="text-xs text-ek-ink/65">· mature {c.from_maturity}→5</span></span>
                          <span className="shrink-0 text-ek-ink/60">{gbp(c.investment_gbp)} → <b className="text-emerald-700">{gbp(c.ale_reduction_gain_gbp)}</b> <span className="text-xs text-ek-purple">({c.marginal_roi}×)</span></span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 overflow-hidden rounded-md border border-black/5 bg-white" data-testid="roi-table">
              <table className="w-full text-sm">
                <thead className="bg-ek-canvas/70 text-left text-[0.7rem] uppercase tracking-wide text-ek-ink/65">
                  <tr>
                    <th className="px-4 py-3">Control</th>
                    <th className="px-4 py-3">Themes</th>
                    <th className="px-4 py-3 text-center">Maturity</th>
                    <th className="px-4 py-3 text-right">Annual spend</th>
                    <th className="px-4 py-3 text-right">Loss removed</th>
                    <th className="px-4 py-3 text-right">ROI</th>
                    <th className="px-4 py-3 text-right">Net benefit</th>
                    {canWrite && <th className="px-4 py-3"></th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.control_id} data-testid={`roi-row-${c.control_id}`} className="border-t border-black/5">
                      <td className="px-4 py-3">
                        <p className="font-medium text-ek-ink">{c.title}</p>
                        <p className="text-[0.7rem] text-ek-ink/65">{c.control_id} · {c.owner_role?.toUpperCase()}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {c.theme_ids.map((t) => (
                            <span key={t} className="rounded-full bg-ek-purple/10 px-2 py-0.5 text-[0.65rem] text-ek-purple">
                              {THEME_LABELS[t] || t}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-ek-ink/60">{c.maturity}/5</td>
                      <td className="px-4 py-3 text-right">
                        {editing === c.control_id
                          ? <EconEditor control={c} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
                          : <span className="tabular-nums text-ek-ink/70">{gbpFull(c.annual_cost_gbp)}</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium text-emerald-700">{gbpFull(c.ale_reduction_gbp)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${c.roi_ratio >= 1 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                          {c.roi_ratio}×
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-right tabular-nums ${c.net_benefit_gbp >= 0 ? "text-ek-ink/70" : "text-red-600"}`}>
                        {c.net_benefit_gbp >= 0 ? "+" : ""}{gbp(c.net_benefit_gbp)}
                      </td>
                      {canWrite && (
                        <td className="px-4 py-3 text-right">
                          {editing !== c.control_id && (
                            <button onClick={() => setEditing(c.control_id)} data-testid={`edit-econ-${c.control_id}`}
                              className="text-ek-ink/30 hover:text-ek-purple"><Pencil size={14} /></button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
